// App del VENDEDOR EN RUTA (pedido de Rodrigo, 2026-10-25: «un Random Móvil propio»). Vive en
// /app/ruta/ (#/vendedor), fuera del shell de escritorio, y se instala en el celular. Con señal baja el
// paquete del día (sus clientes con deuda y «suele comprar», catálogo con precios y stock); sin señal
// trabaja con lo guardado y la cola manda los pedidos y visitas cuando vuelve la conexión.
// Pantallas = COPIA LITERAL de la app de pedidos directos del B2B (madeci-b2b/ventas/index.html, pedido
// de Rodrigo 26-10: «literal déjalo como el B2B»): mismo marcado y mismas clases. Lo propio del ERP:
// elegir entre los clientes asignados (Ruta de hoy / Mis clientes / Todos), documento y sin señal.
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ErrorApi } from '../api/cliente';
import { coincide } from '../comun/coincide';
import { tituloDte } from '../dte/pdfDte';
import { CambiarClave } from '../sesion/CambiarClave';
import { useSesion } from '../sesion/SesionContexto';
import {
  CLAVES,
  escribir,
  leer,
  type CatalogoVendedor,
  type ClienteRuta,
  type ContextoVendedor,
  type Paquete,
} from './almacen';
import { ProveedorCola, useCola } from './cola';
import { pdfDelCliente } from './Cuenta';
import { MisCobros } from './MisCobros';
import {
  condicionTexto,
  diaDeSemana,
  esContado,
  ESTADO_PEDIDO,
  hoyChile,
  NOMBRE_DIA,
  pesos,
  rutaDelDia,
  rutConDv,
} from './logica';
import { Pasos, TomarPedido, type Terminado } from './Pedido';
import { VisorPdf, type DocumentoPdf } from './VisorPdf';
import './vendedor.css';

type Vista =
  | { tipo: 'clientes' }
  | { tipo: 'historial' }
  | { tipo: 'pedido'; clienteId: string }
  | { tipo: 'listo'; clienteId: string; hecho: Terminado };

/** Con señal, el paquete se renueva solo si tiene más de esto (precios y stock cambian en el día). */
const VIGENCIA_MS = 60 * 60_000;

/** Pone (o reemplaza) un <link>/<meta> del head; index.html trae los de Bodega por defecto. */
function fijarEnHead(tag: 'link' | 'meta', clave: string, valor: string, atributo: 'href' | 'content') {
  const sel = tag === 'link' ? `link[rel="${clave}"]` : `meta[name="${clave}"]`;
  let el = document.head.querySelector<HTMLLinkElement | HTMLMetaElement>(sel);
  if (!el) {
    el = document.createElement(tag);
    if (tag === 'link') (el as HTMLLinkElement).rel = clave;
    else (el as HTMLMetaElement).name = clave;
    document.head.appendChild(el);
  }
  el.setAttribute(atributo, valor);
}

/** El manifiesto y el service worker solo se registran al entrar a la app del vendedor. */
function instalable() {
  try {
    // Sin esto el celular instala «Bodega» (el manifiesto de index.html) en vez de «MADECI Ruta».
    fijarEnHead('link', 'manifest', '/vendedor.webmanifest', 'href');
    fijarEnHead('link', 'apple-touch-icon', '/vendedor-180.png', 'href');
    fijarEnHead('meta', 'apple-mobile-web-app-title', 'MADECI Ruta', 'content');
    fijarEnHead('meta', 'theme-color', '#1a1a1a', 'content');
    if ('serviceWorker' in navigator && window.isSecureContext) {
      // Al abrir busca versión nueva; si llega una, recarga UNA vez para correr el código nuevo.
      const habia = !!navigator.serviceWorker.controller;
      let recargada = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (habia && !recargada) {
          recargada = true;
          window.location.reload();
        }
      });
      void navigator.serviceWorker
        .register('/sw-vendedor.js', { updateViaCache: 'none' })
        .then((r) => r.update())
        .catch(() => undefined);
    }
  } catch {
    /* sin instalación: la app sigue funcionando en el navegador */
  }
}

export function Vendedor({ sinConexion = false }: { sinConexion?: boolean }) {
  const qc = useQueryClient();
  return (
    <ProveedorCola
      alEnviar={() => void qc.invalidateQueries({ queryKey: ['vendedor', 'visitas'] })}
    >
      <AppVendedor sinConexion={sinConexion} />
    </ProveedorCola>
  );
}

function AppVendedor({ sinConexion }: { sinConexion: boolean }) {
  const s = useSesion();
  const cola = useCola();
  const [paquete, setPaquete] = useState<Paquete | null>(null);
  const [leido, setLeido] = useState(false);
  const [bajando, setBajando] = useState(false);
  const [errorBajada, setErrorBajada] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>({ tipo: 'clientes' });
  // Clientes de otra cartera que la gerencia eligió al buscar en «Todos» (no se guardan).
  const [otros, setOtros] = useState<ClienteRuta[]>([]);
  const [menu, setMenu] = useState(false);
  const [clave, setClave] = useState(false);

  useEffect(instalable, []);
  useEffect(() => {
    void leer<Paquete>(CLAVES.paquete).then((p) => {
      setPaquete(p);
      setLeido(true);
    });
  }, []);
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
    } catch {
      /* jsdom */
    }
  }, [vista.tipo]);

  const empresa = s.empresa;
  const conSesion = !sinConexion && s.estado === 'autenticado' && !!empresa;
  // Un paquete de otra empresa o de otro usuario no sirve; tampoco uno bajado «como» otro vendedor
  // (ya no se elige: cada uno entra con su vendedor y la gerencia elige el de cada pedido, 27-10).
  const propio =
    paquete &&
    !paquete.verComo &&
    (!conSesion ||
      (paquete.empresaId === empresa!.id && paquete.usuarioNombre === (s.usuario?.nombre ?? '')))
      ? paquete
      : null;

  const bajar = useCallback(
    async () => {
      if (!conSesion) return;
      setBajando(true);
      setErrorBajada(null);
      try {
        const query = { empresaId: empresa!.id };
        const [contexto, clientes, catalogo] = await Promise.all([
          api.get<ContextoVendedor>('/vendedor/contexto', { query }),
          api.get<ClienteRuta[]>('/vendedor/clientes', { query }),
          api.get<CatalogoVendedor>('/vendedor/catalogo', { query }),
        ]);
        const nuevo: Paquete = {
          empresaId: empresa!.id,
          empresaNombre: empresa!.razon_social ?? '',
          usuarioNombre: s.usuario?.nombre ?? '',
          verComo: null,
          contexto,
          clientes,
          catalogo,
          guardadoEn: new Date().toISOString(),
        };
        await escribir(CLAVES.paquete, nuevo);
        setPaquete(nuevo);
      } catch (e) {
        setErrorBajada(e instanceof Error ? e.message : String(e));
      } finally {
        setBajando(false);
      }
    },
    [conSesion, empresa, s.usuario?.nombre],
  );

  useEffect(() => {
    if (!leido || !conSesion || bajando) return;
    const viejo =
      !propio ||
      Date.now() - new Date(propio.guardadoEn).getTime() > VIGENCIA_MS ||
      // Bajado antes del 27-10: sin listas alternativas ni condiciones por cliente.
      !Array.isArray(propio.catalogo.listasAlternativas) ||
      propio.clientes.some((c) => !Array.isArray(c.opcionesPago));
    if (viejo && !errorBajada) void bajar();
    // Solo al abrir (y si el paquete no sirve): el vendedor actualiza a mano con «Actualizar».
  }, [leido, conSesion]);

  const nombreUsuario = s.usuario?.nombre ?? paquete?.usuarioNombre ?? '';
  const vendedor = propio?.contexto.vendedor;
  const puedeErp = s.puede('caja.turno.operar') || s.puede('ventas.nota.cambiar_vendedor');
  const porEnviar = cola.pendientes.length;
  const enLinea = !sinConexion && cola.enLinea;
  const clienteDe = (id: string) =>
    propio?.clientes.find((c) => c.entidadId === id) ?? otros.find((c) => c.entidadId === id);

  return (
    <div className="vend">
      <header className="top">
        <div className="logo">
          MADECI<span>.</span>
          <small>Venta en ruta{vendedor ? ` · ${vendedor.codigo}` : ''}</small>
        </div>
        <div className="sp" />
        <button
          className={`navbtn${vista.tipo !== 'historial' ? ' on' : ''}`}
          type="button"
          onClick={() => setVista(vista.tipo === 'historial' ? { tipo: 'clientes' } : vista)}
        >
          Nuevo pedido
        </button>
        <button
          className={`navbtn${vista.tipo === 'historial' ? ' on' : ''}`}
          type="button"
          onClick={() => setVista({ tipo: 'historial' })}
        >
          Digitados{porEnviar ? ` (${porEnviar})` : ''}
        </button>
        <span className="quien">{nombreUsuario}</span>
        <button
          className={`navbtn${menu ? ' on' : ''}`}
          type="button"
          aria-label="Menú"
          aria-expanded={menu}
          onClick={() => setMenu((m) => !m)}
        >
          ☰
        </button>
      </header>
      {menu && (
        <nav className="menu" aria-label="Menú">
          <div className="meta">
            {import.meta.env['VITE_VERSION'] ? `Versión ${import.meta.env['VITE_VERSION']} · ` : ''}
            <span className={`estado-senal${enLinea ? '' : ' sin'}`} />{' '}
            {enLinea ? 'En línea' : 'Sin señal'} ·{' '}
            {propio
              ? `datos del ${new Date(propio.guardadoEn).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}`
              : 'sin datos guardados'}
          </div>
          <button
            type="button"
            disabled={!conSesion || bajando}
            onClick={() => {
              setMenu(false);
              void bajar();
            }}
          >
            {bajando ? 'Actualizando…' : 'Actualizar clientes, precios y stock'}
          </button>
          {conSesion && (
            <button type="button" onClick={() => (setMenu(false), setClave(true))}>
              Cambiar mi clave
            </button>
          )}
          {puedeErp && (
            <a href="#/" onClick={() => setMenu(false)}>
              Ir al ERP
            </a>
          )}
          <button type="button" disabled={!conSesion} onClick={() => void s.cerrar()}>
            Salir
          </button>
        </nav>
      )}
      {clave && <CambiarClave alCerrar={() => setClave(false)} />}
      {!enLinea && <div className="barra-random">Sin señal: los pedidos quedan en el celular y se envían solos al volver.</div>}
      {bajando && <div className="barra-random">Bajando clientes, precios y stock…</div>}
      {porEnviar > 0 && vista.tipo !== 'historial' && (
        <button
          type="button"
          className="barra-random"
          style={{ width: '100%' }}
          onClick={() => setVista({ tipo: 'historial' })}
        >
          {porEnviar} por enviar · toca para verlos
        </button>
      )}
      {cola.sinSesion && (
        <div className="barra-random">
          Tu sesión venció: vuelve a ingresar para que salgan los pedidos en cola (no se pierden).
        </div>
      )}
      {errorBajada && (
        <div className="barra-random">
          No se pudieron bajar los datos: {errorBajada}
          {propio ? ' Se usan los guardados.' : ''}
        </div>
      )}
      {!leido ? (
        <div className="wrap">
          <div className="vacio">Cargando…</div>
        </div>
      ) : !propio ? (
        <div className="wrap">
          <div className="caja">
            <h3>Primera vez en este celular</h3>
            <p className="h-sub">
              {conSesion
                ? bajando
                  ? 'Bajando tus clientes, precios y stock…'
                  : 'No se pudieron bajar tus datos. Revisa la señal y toca «Reintentar».'
                : 'Necesitas señal la primera vez para bajar tus clientes, precios y stock. Después funciona sin internet.'}
            </p>
            {conSesion && !bajando && (
              <button type="button" className="btn" onClick={() => void bajar()}>
                Reintentar
              </button>
            )}
          </div>
        </div>
      ) : vista.tipo === 'pedido' && clienteDe(vista.clienteId) ? (
        <TomarPedido
          key={vista.clienteId}
          paquete={propio}
          cliente={clienteDe(vista.clienteId)!}
          alCambiarCliente={() => setVista({ tipo: 'clientes' })}
          alTerminar={(hecho) => setVista({ tipo: 'listo', clienteId: vista.clienteId, hecho })}
        />
      ) : vista.tipo === 'listo' ? (
        <Listo
          cliente={clienteDe(vista.clienteId) ?? null}
          hecho={vista.hecho}
          enLinea={enLinea}
          vendedor={vendedor ? `${vendedor.codigo} ${vendedor.nombre}` : ''}
          otroCliente={() => setVista({ tipo: 'clientes' })}
          mismoCliente={() => setVista({ tipo: 'pedido', clienteId: vista.clienteId })}
        />
      ) : vista.tipo === 'historial' ? (
        <div className="wrap">
          <MisPedidos paquete={propio} />
        </div>
      ) : (
        <ElegirCliente
          paquete={propio}
          enLinea={enLinea && conSesion}
          elegir={(c) => {
            if (!propio.clientes.some((x) => x.entidadId === c.entidadId))
              setOtros((xs) => [...xs.filter((x) => x.entidadId !== c.entidadId), c]);
            setVista({ tipo: 'pedido', clienteId: c.entidadId });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- PASO 1 — elegir cliente

/** Lo que el vendedor ya hizo hoy con cada cliente (enviado o en cola). */
function useHechoHoy(paquete: Paquete) {
  const cola = useCola();
  return useMemo(() => {
    const hoy = hoyChile();
    const m = new Map<string, 'PEDIDO' | 'VISITA'>();
    for (const c of paquete.clientes)
      if (c.ultimaVisita?.fecha === hoy)
        m.set(c.entidadId, c.ultimaVisita.resultado === 'SIN_VENTA' ? 'VISITA' : 'PEDIDO');
    for (const x of [...cola.enviados, ...cola.pendientes])
      if (hoyChile(new Date(x.creadoEn)) === hoy) {
        const id = String(x.cuerpo['clienteEntidadId']);
        if (m.get(id) !== 'PEDIDO') m.set(id, x.tipo);
      }
    return m;
  }, [paquete.clientes, cola.enviados, cola.pendientes]);
}

function ElegirCliente({
  paquete,
  enLinea,
  elegir,
}: {
  paquete: Paquete;
  enLinea: boolean;
  elegir: (c: ClienteRuta) => void;
}) {
  const hoy = diaDeSemana(hoyChile());
  const deHoy = rutaDelDia(paquete.clientes, hoy);
  const puedeTodos = !!paquete.contexto.vendedores;
  // Gerencia (puede cambiar el vendedor) parte en todos los clientes; el vendedor, en su ruta.
  const [filtro, setFiltro] = useState<'hoy' | 'mios' | 'todos'>(
    puedeTodos ? 'todos' : deHoy.length ? 'hoy' : 'mios',
  );
  const [texto, setTexto] = useState('');
  const hecho = useHechoHoy(paquete);
  const q = texto.trim();
  const listas = new Map(paquete.catalogo.listas.map((l) => [l.id, l.codigo]));
  // Todos los clientes de la empresa: sin texto, el maestro completo por nombre de a 50 («Ver más»).
  const porPagina = q ? 30 : 50;
  const todos = useInfiniteQuery({
    queryKey: ['vendedor', 'buscar', paquete.empresaId, q],
    queryFn: ({ pageParam }) =>
      api.get<ClienteRuta[]>('/vendedor/clientes/buscar', {
        query: { empresaId: paquete.empresaId, texto: q, desde: pageParam },
      }),
    initialPageParam: 0,
    getNextPageParam: (ultima, todas) =>
      ultima.length === porPagina ? todas.reduce((n, x) => n + x.length, 0) : undefined,
    enabled: filtro === 'todos' && enLinea && q.length !== 1,
    staleTime: 60_000,
  });
  const base = filtro === 'hoy' ? deHoy : paquete.clientes;
  const lista =
    filtro === 'todos'
      ? (todos.data?.pages.flat() ?? [])
      : base
          .filter((c) =>
            coincide(
              texto,
              c.razonSocial,
              c.nombreFantasia,
              c.rut,
              ...c.direcciones.map((d) => d.texto),
            ),
          )
          .slice(0, 120);
  const hechosHoy = deHoy.filter((c) => hecho.has(c.entidadId)).length;
  return (
    <div className="wrap">
      <Pasos paso={1} />
      <h2 className="tit">¿Para qué cliente es el pedido?</h2>
      <p className="sub">
        {filtro === 'todos'
          ? 'El maestro completo de clientes. Busca por nombre o por RUT.'
          : filtro === 'hoy'
            ? `Tu ruta del ${NOMBRE_DIA[hoy]!.toLowerCase()}: ${hechosHoy} de ${deHoy.length} visitados. Busca por nombre o por RUT.`
            : 'Busca por nombre o por RUT. Aparecen tus clientes.'}
      </p>
      <div className="tabs">
        <button
          className={`tab${filtro === 'hoy' ? ' on' : ''}`}
          type="button"
          onClick={() => setFiltro('hoy')}
        >
          Ruta de hoy ({deHoy.length})
        </button>
        <button
          className={`tab${filtro === 'mios' ? ' on' : ''}`}
          type="button"
          onClick={() => setFiltro('mios')}
        >
          Mis clientes ({paquete.clientes.length})
        </button>
        {puedeTodos && (
          <button
            className={`tab${filtro === 'todos' ? ' on' : ''}`}
            type="button"
            onClick={() => setFiltro('todos')}
          >
            Todos
          </button>
        )}
      </div>
      <div className="buscador">
        <input
          type="search"
          placeholder="Nombre o RUT del cliente…"
          aria-label="Nombre o RUT del cliente"
          autoComplete="off"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        <button className="limpiar" type="button" aria-label="Limpiar" onClick={() => setTexto('')}>
          ×
        </button>
      </div>
      {filtro === 'todos' && !enLinea ? (
        <div className="vacio">Para buscar en todos los clientes necesitas señal.</div>
      ) : filtro === 'todos' && q.length === 1 ? (
        <div className="vacio">Escribe al menos 2 letras del nombre o el RUT.</div>
      ) : filtro === 'todos' && todos.isFetching && !todos.data ? (
        <div className="vacio">Buscando…</div>
      ) : filtro === 'todos' && todos.isError ? (
        <div className="error">{(todos.error as Error).message}</div>
      ) : !lista.length ? (
        <div className="vacio">
          {q ? (
            <>
              No encontramos ningún cliente con eso.
              <br />
              Prueba con otra parte del nombre o con el RUT.
            </>
          ) : filtro === 'hoy' ? (
            'Hoy no tienes clientes en ruta: búscalo en «Mis clientes».'
          ) : (
            'No tienes clientes asignados. Pídelo en la oficina.'
          )}
        </div>
      ) : (
        <div>
          {lista.map((c) => {
            const d = c.direcciones.find((x) => x.principal) ?? c.direcciones[0];
            const h = hecho.get(c.entidadId);
            const vencidos = Number(c.deuda?.vencidos ?? 0);
            return (
              <button
                key={c.entidadId}
                className="cli-card"
                type="button"
                onClick={() => elegir(c)}
              >
                <div className="nom">{c.nombreFantasia || c.razonSocial}</div>
                <div className="meta">
                  RUT {rutConDv(c)}
                  {d?.texto ? ` · ${d.texto}` : ''}
                  {d?.comuna ? `, ${d.comuna}` : ''}
                </div>
                <div style={{ marginTop: 7 }}>
                  <span className="chip gris">Lista {listas.get(c.listaPrecioId ?? '') ?? '—'}</span>
                  <span className={`chip ${esContado(c) ? 'verde' : 'indigo'}`}>
                    {condicionTexto(c)}
                  </span>
                  {filtro === 'todos' && c.vendedor && (
                    <span className="chip gris">{c.vendedor}</span>
                  )}
                  {c.bloqueado && <span className="chip rojo">BLOQUEADO</span>}
                  {vencidos > 0 && <span className="chip rojo">Vencido {pesos(vencidos)}</span>}
                  {h && (
                    <span className={`chip ${h === 'PEDIDO' ? 'verde' : 'gris'}`}>
                      {h === 'PEDIDO' ? '✓ pedido hoy' : 'visitado hoy'}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
          {filtro === 'todos' && todos.hasNextPage && (
            <button
              type="button"
              className="btn sec block"
              disabled={todos.isFetchingNextPage}
              onClick={() => void todos.fetchNextPage()}
            >
              {todos.isFetchingNextPage ? 'Cargando…' : 'Ver más clientes'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- PASO 4 — resultado

function Listo({
  cliente,
  hecho,
  enLinea,
  vendedor,
  otroCliente,
  mismoCliente,
}: {
  cliente: ClienteRuta | null;
  hecho: Terminado;
  enLinea: boolean;
  vendedor: string;
  otroCliente: () => void;
  mismoCliente: () => void;
}) {
  const nombre = cliente ? cliente.nombreFantasia || cliente.razonSocial : '';
  return (
    <div className="wrap">
      <div className="caja" style={{ textAlign: 'center', padding: '30px 22px' }}>
        <div style={{ fontSize: 44, lineHeight: 1 }}>{hecho.tipo === 'PEDIDO' ? '🍾' : '📝'}</div>
        <h2 className="tit" style={{ marginTop: 10 }}>
          {hecho.tipo === 'PEDIDO' ? 'Pedido registrado' : 'Visita registrada'}
        </h2>
        <p className="sub">
          {nombre}
          {hecho.tipo === 'PEDIDO' ? ` · ${pesos(hecho.total)}` : ' · no compró'}
        </p>
        <div style={{ textAlign: 'left', marginTop: 18 }}>
          {hecho.tipo === 'PEDIDO' ? (
            <div className="ok">
              {enLinea
                ? `✅ Se está enviando al ERP como Nota de Venta${vendedor ? ` del vendedor ${vendedor}` : ''}. El número y el estado los ves en «Mis pedidos».`
                : '✅ Quedó guardado en el celular. Se envía solo al ERP cuando vuelva la señal.'}
            </div>
          ) : (
            <div className="ok">✅ Quedó anotado que el cliente no compró hoy.</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <button className="btn sec" style={{ flex: 1 }} type="button" onClick={otroCliente}>
            Otro cliente
          </button>
          <button className="btn" style={{ flex: 1 }} type="button" onClick={mismoCliente}>
            Otro pedido para este cliente
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- pedidos digitados

interface VisitaServidor {
  id: string;
  idExterno: string;
  fecha: string;
  resultado: 'PEDIDO' | 'SIN_VENTA' | 'SIN_STOCK';
  comentario: string | null;
  ajustes: Array<{ sku: string; descripcion: string; pedido: string; entro: string }>;
  tomadoEn: string;
  clienteId: string;
  cliente: string;
  notaId: string | null;
  numero: string | null;
  estado: string | null;
  total: string | null;
  /** Factura o boleta ya emitida de la nota (respuestas viejas no la traen). */
  documentos?: Array<{ documentoId: string; tipoDte: number; folio: number }>;
}

const TONO: Record<string, string> = { ok: 'verde', aviso: 'ambar', malo: 'rojo', neutro: 'gris' };

function Estado({ estado }: { estado: string }) {
  const e = ESTADO_PEDIDO[estado] ?? { texto: estado, tono: 'neutro' as const };
  return <span className={`chip ${TONO[e.tono]}`}>{e.texto}</span>;
}

function MisPedidos({ paquete }: { paquete: Paquete }) {
  const s = useSesion();
  const cola = useCola();
  const enLinea = s.estado === 'autenticado' && cola.enLinea;
  const q = useQuery({
    queryKey: ['vendedor', 'visitas', paquete.empresaId],
    queryFn: () =>
      api.get<VisitaServidor[]>('/vendedor/visitas', {
        query: { empresaId: paquete.empresaId },
      }),
    enabled: enLinea,
    refetchInterval: 30_000,
  });
  const delServidor = q.data ?? [];
  // Quien puede firmar autoriza sus pedidos que quedaron esperando firma (Rodrigo 27-10).
  const puedeFirmar = s.puede('ventas.nota.aprobar');
  const [pdf, setPdf] = useState<DocumentoPdf | null>(null);
  const [firmando, setFirmando] = useState<string | null>(null);
  const [errorFirma, setErrorFirma] = useState<string | null>(null);
  const autorizar = async (notaId: string) => {
    setFirmando(notaId);
    setErrorFirma(null);
    try {
      await api.post(
        `/vendedor/pedidos/${notaId}/autorizar`,
        { empresaId: paquete.empresaId },
        { idempotencia: `aut-${notaId}-${Date.now()}` },
      );
      await q.refetch();
    } catch (e) {
      setErrorFirma(e instanceof Error ? e.message : String(e));
    } finally {
      setFirmando(null);
    }
  };
  const enServidor = new Set(delServidor.map((v) => v.idExterno));
  // Sin señal se muestra lo que el celular envió (lo del servidor manda cuando hay).
  const soloLocales = cola.enviados.filter((x) => !enServidor.has(x.id));
  return (
    <>
      {s.puede('cobranza.ruta.recibir') && <MisCobros paquete={paquete} />}
      <h2 className="tit">Mis pedidos</h2>
      <p className="sub">Lo digitado en los últimos 7 días, con su número y estado en el ERP.</p>
      {cola.pendientes.length > 0 && (
        <section className="caja">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            Por enviar · {cola.pendientes.length}
            <button
              type="button"
              className="btn sm"
              style={{ marginLeft: 'auto' }}
              disabled={cola.enviando}
              onClick={cola.enviarAhora}
            >
              {cola.enviando ? 'Enviando…' : 'Enviar ahora'}
            </button>
          </h3>
          <p className="h-sub">Quedan en el celular y salen solos cuando hay señal.</p>
          {cola.pendientes.map((x) => (
            <div key={x.id} className="ped">
              <div className="cuerpo">
                <div className="nom">{x.cliente}</div>
                <div className="meta">
                  {x.tipo === 'PEDIDO'
                    ? `Pedido · ${x.lineas} producto${x.lineas === 1 ? '' : 's'} · ${pesos(x.totalAprox)} aprox.`
                    : 'Visita sin venta'}{' '}
                  · {new Date(x.creadoEn).toLocaleTimeString('es-CL', { timeStyle: 'short' })}
                </div>
                {x.error && (
                  <div className="meta" style={{ color: 'var(--rojo)', fontWeight: 600 }}>
                    {x.error}
                  </div>
                )}
                {x.estado === 'ERROR' && (
                  <div className="botones">
                    <button
                      type="button"
                      className="btn sec sm"
                      onClick={() => void cola.reintentar(x.id)}
                    >
                      Reintentar
                    </button>
                    <button
                      type="button"
                      className="btn sec sm"
                      onClick={() => {
                        if (window.confirm('¿Descartar este pedido? No se enviará.'))
                          void cola.descartar(x.id);
                      }}
                    >
                      Descartar
                    </button>
                  </div>
                )}
              </div>
              <span className={`chip ${x.estado === 'ERROR' ? 'rojo' : 'ambar'}`}>
                {x.estado === 'ERROR' ? 'rechazado' : 'en cola'}
              </span>
            </div>
          ))}
        </section>
      )}
      {q.isError && !(q.error instanceof ErrorApi && q.error.status === 0) && (
        <div className="error">{q.error.message}</div>
      )}
      {errorFirma && <div className="error">{errorFirma}</div>}
      {!enLinea && <p className="sub">Sin señal: se muestra lo enviado desde este celular.</p>}
      {!delServidor.length && !soloLocales.length ? (
        <div className="vacio">Todavía no hay pedidos digitados acá.</div>
      ) : (
        <div>
          {soloLocales.map((x) => (
            <div key={x.id} className="ped">
              <div className="cuerpo">
                <div className="nom">
                  {x.resultado?.numero ? `N° ${x.resultado.numero} · ` : ''}
                  {x.cliente}
                </div>
                <div className="meta">
                  {x.tipo === 'VISITA' ? 'Visita sin venta' : `${x.lineas} producto(s)`}
                </div>
                <Ajustes ajustes={x.resultado?.ajustes ?? []} />
                {x.resultado?.aviso && (
                  <div className="meta" style={{ color: 'var(--rojo)' }}>
                    {x.resultado.aviso}
                  </div>
                )}
              </div>
              <div>
                {x.resultado ? (
                  <Estado estado={x.resultado.estado} />
                ) : (
                  <span className="chip gris">visitado</span>
                )}
              </div>
              <div className="tot">{x.resultado?.total ? pesos(x.resultado.total) : ''}</div>
            </div>
          ))}
          {delServidor.map((v) => (
            <div key={v.id} className="ped">
              <div className="cuerpo">
                <div className="nom">
                  {v.numero ? `N° ${v.numero} · ` : ''}
                  {v.cliente}
                </div>
                <div className="meta">
                  {v.fecha.slice(8, 10)}-{v.fecha.slice(5, 7)}
                  {v.resultado === 'SIN_VENTA'
                    ? ` · no compró${v.comentario ? ` · ${v.comentario}` : ''}`
                    : ''}
                </div>
                <Ajustes ajustes={v.ajustes} />
                {!!v.documentos?.length && (
                  <div className="meta">
                    {v.documentos.map((d) => {
                      const titulo = tituloDte(d.tipoDte, d.folio);
                      return (
                        <button
                          key={d.documentoId}
                          type="button"
                          className="link doc"
                          title="Ver el PDF"
                          disabled={!enLinea}
                          onClick={() =>
                            setPdf(
                              pdfDelCliente(paquete.empresaId, v.clienteId, d.documentoId, titulo),
                            )
                          }
                        >
                          {titulo}
                        </button>
                      );
                    })}
                  </div>
                )}
                {puedeFirmar && enLinea && v.notaId && v.estado === 'EN_APROBACION' && (
                  <div className="botones">
                    <button
                      type="button"
                      className="btn verde sm"
                      disabled={firmando !== null}
                      onClick={() => void autorizar(v.notaId!)}
                    >
                      {firmando === v.notaId ? 'Autorizando…' : 'Autorizar'}
                    </button>
                  </div>
                )}
              </div>
              <div>
                {v.resultado === 'SIN_VENTA' ? (
                  <span className="chip gris">visitado</span>
                ) : (
                  <Estado estado={v.resultado === 'SIN_STOCK' ? 'SIN_STOCK' : (v.estado ?? '')} />
                )}
              </div>
              <div className="tot">{v.total ? pesos(v.total) : ''}</div>
            </div>
          ))}
        </div>
      )}
      {pdf && <VisorPdf doc={pdf} alCerrar={() => setPdf(null)} />}
    </>
  );
}

function Ajustes({
  ajustes,
}: {
  ajustes: Array<{ sku: string; descripcion: string; pedido: string; entro: string }>;
}) {
  if (!ajustes.length) return null;
  return (
    <div className="meta" style={{ color: 'var(--rojo)', fontWeight: 600 }}>
      Sin stock suficiente:{' '}
      {ajustes.map((a) => `${a.descripcion} pedidas ${a.pedido}, entraron ${a.entro}`).join(' · ')}{' '}
      (en unidades)
    </div>
  );
}
